/**
 * ADR 358: the boot fast path. `keycloak.init` with no tokens and
 * `login-required` costs two full navigations through the auth origin (auth
 * redirect + code exchange) on EVERY page load — measured at ~3 s of the JSL
 * /live load over the demo path. Seeding init with the previous session's
 * token trio turns that into at most one silent refresh POST.
 *
 * sessionStorage on purpose: tab-scoped and gone when the tab closes. The
 * access token already lives in JS memory (siteStore) for API calls, so the
 * XSS exposure class is unchanged — a fresh tab still does the full
 * handshake. Every accessor try/catches: storage can be absent or throw
 * (private windows, storage-blocked browsers), and the fallback is simply
 * the slow path that always existed.
 */

const KEY = "twin.kc.tokens";

export interface CachedTokens {
  token: string;
  refreshToken: string;
  idToken?: string;
}

export function saveTokens(kc: { token?: string; refreshToken?: string; idToken?: string }): void {
  if (!kc.token || !kc.refreshToken) return;
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify({
      token: kc.token, refreshToken: kc.refreshToken, idToken: kc.idToken,
    }));
  } catch { /* storage unavailable — slow path next boot */ }
}

export function loadTokens(): CachedTokens | null {
  try {
    const raw = window.sessionStorage.getItem(KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as Partial<CachedTokens>;
    return typeof v.token === "string" && typeof v.refreshToken === "string"
      ? { token: v.token, refreshToken: v.refreshToken, idToken: v.idToken }
      : null;
  } catch {
    return null;
  }
}

export function clearTokens(): void {
  try { window.sessionStorage.removeItem(KEY); } catch { /* nothing to clear */ }
}
